// Pure rules for the hub's Meta Pixel. Nothing here touches the page, so every
// rule can be tested on its own. The pixel sends an event's fixed name and, for
// one of them, a value; it never sends what a person answered, chose or booked.

export const PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
export const PIXEL_COOKIE_DOMAIN = '.talktime.ae';

// Empty or malformed means the pixel is off. Nuxt turns an all-digit environment value
// into a number, so a safe integer counts as well as a string.
export const normalisePixelId = (value: unknown): string => {
   const id =
      typeof value === 'string' ? value.trim() : Number.isSafeInteger(value) ? String(value) : '';
   return /^\d{5,32}$/.test(id) ? id : '';
};

// The onboarding questions that are reported. pixelStepEventName gives each one's event.
export const PIXEL_STEPS = [
   'coverage',
   'therapy_type',
   'therapy_reasons',
   'age_group',
   'gender',
   'therapist_preference',
] as const;
export type PixelStep = (typeof PIXEL_STEPS)[number];

// The register route that owns each question. Keyed by route, not by the
// acquisition stage, because that stage lumps four of these questions together.
export const PIXEL_ROUTE_STEPS: Readonly<Record<string, PixelStep>> = {
   'auth-client-register-coverage': 'coverage',
   'auth-client-register-therapy-type': 'therapy_type',
   'auth-client-register-therapy-reasons': 'therapy_reasons',
   'auth-client-register-age-groups': 'age_group',
   'auth-client-register-client-gender': 'gender',
   'auth-client-register-therapist-preference': 'therapist_preference',
};

export const pixelStepForRoute = (routeName: unknown): PixelStep | undefined =>
   typeof routeName === 'string' && Object.hasOwn(PIXEL_ROUTE_STEPS, routeName)
      ? PIXEL_ROUTE_STEPS[routeName]
      : undefined;

// Each step is its own event. Meta runs this pixel in its protected mode, which drops
// custom parameters and trims the page address, so the event name is the only thing
// that can tell one step from another.
export const pixelStepEventName = (step: PixelStep) => `Onboarding_${step}`;

export type PixelParamKind = 'none' | 'value';
export type PixelEventRule = {
   method: 'track' | 'trackCustom';
   // Sent at most once per journey.
   once: boolean;
   // Waits for a clean address instead of being dropped.
   hold: boolean;
   params: PixelParamKind;
};

// Every event the hub can send. A name that is not a key here never reaches the
// library, and Purchase is not one: the backend alone sends it. PageView is not one
// either: only a finished navigation sends it (notePixelPageView).
export const PIXEL_EVENTS: Readonly<Record<string, PixelEventRule>> = {
   ...Object.fromEntries(
      PIXEL_STEPS.map((step): [string, PixelEventRule] => [
         pixelStepEventName(step),
         { method: 'trackCustom', once: false, hold: true, params: 'none' },
      ]),
   ),
   SignupStarted: { method: 'trackCustom', once: true, hold: true, params: 'none' },
   CompleteRegistration: { method: 'track', once: false, hold: true, params: 'none' },
   InitiateCheckout: { method: 'track', once: false, hold: true, params: 'value' },
   TherapistListViewed: { method: 'trackCustom', once: true, hold: true, params: 'none' },
   TherapistChosen: { method: 'trackCustom', once: false, hold: true, params: 'none' },
   ViewContent: { method: 'track', once: true, hold: true, params: 'none' },
   Schedule: { method: 'track', once: false, hold: true, params: 'none' },
};

// The analytics events the app already reports through useSegment().track, and the
// pixel event each one stands for. An event that is not listed goes nowhere, and
// the properties of a listed one are never sent.
export const PIXEL_SEGMENT_EVENTS: Readonly<Record<string, string>> = {
   'Therapist Selected': 'TherapistChosen',
   'Booking Completed': 'Schedule',
};

export const pixelEventForSegment = (event: unknown): string | undefined =>
   typeof event === 'string' && Object.hasOwn(PIXEL_SEGMENT_EVENTS, event)
      ? PIXEL_SEGMENT_EVENTS[event]
      : undefined;

// The only parameters that may reach the library, rebuilt from scratch so nothing
// else on the object can ride along. `null` means the event is refused.
export const sanitisePixelParams = (
   name: string,
   params: unknown,
): Record<string, string | number> | null => {
   if (!Object.hasOwn(PIXEL_EVENTS, name)) return null;
   const input = params && typeof params === 'object' ? (params as Record<string, unknown>) : {};
   const kind = PIXEL_EVENTS[name].params;
   if (kind === 'none') return {};
   const { value, currency } = input;
   if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
   if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null;
   return { value, currency };
};

export type PixelAddress = { pathname: string; search: string; hash: string };

// The only pages an event may fire from. Meta receives the address, and in a mental
// health product the address itself can say something: /client/chat, /client/journal,
// /client/flaged and /insurance-pending all pass a check for ids. A page that is not
// listed keeps its events held until the person reaches one that is.
export const PIXEL_ALLOWED_PATHS: ReadonlySet<string> = new Set([
   '/',
   '/auth/client/login',
   '/auth/client/signup',
   '/auth/client/register/coverage',
   '/auth/client/register/therapy-type',
   '/auth/client/register/therapy-reasons',
   '/auth/client/register/age-groups',
   '/auth/client/register/client-gender',
   '/auth/client/register/therapist-preference',
   '/therapist-recommendations',
   '/client',
   '/client/packages',
   '/client/benefit-packages',
   '/client/booking',
   '/client/session',
]);

const withoutTrailingSlash = (pathname: string) =>
   pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

// The first booking happens on these exact addresses, and neither query says
// anything about the person.
const PIXEL_ALLOWED_BOOKING_QUERIES: ReadonlySet<string> = new Set(['?action=success', '?welcome=true']);

// Keys an ad link or the language switch adds. They name a campaign, a click or a
// language, never the person, so an address carrying only these stays clean. The
// library still reads them from the address; for this pixel Meta's protected mode
// cuts `dl` and `rl` down to the origin in the browser before anything is sent.
const isHarmlessQueryKey = (key: string) =>
   /^utm_[a-z0-9_]{1,60}$/i.test(key) || ['fbclid', 'gclid', 'lang'].includes(key.toLowerCase());

// An email address dropped into a campaign value by a mail merge is still personal.
const isHarmlessQuery = (search: string) => {
   const params = [...new URLSearchParams(search)];
   return (
      params.length > 0 &&
      params.every(([key, value]) => isHarmlessQueryKey(key) && !value.includes('@'))
   );
};

const isPixelQueryClean = (pathname: string, search: string) =>
   !search ||
   isHarmlessQuery(search) ||
   (pathname === '/client/booking' && PIXEL_ALLOWED_BOOKING_QUERIES.has(search));

export const isPixelAddressClean = (address: PixelAddress, hasRouteParams = false): boolean => {
   const pathname = withoutTrailingSlash(address.pathname);
   return (
      !hasRouteParams &&
      !address.hash &&
      isPixelQueryClean(pathname, address.search) &&
      PIXEL_ALLOWED_PATHS.has(pathname)
   );
};

// The library also sends the page the browser came from. A browser trims another
// site's address to its origin, so only a same-origin referrer (a reload inside the
// hub) can carry a full address, and the same rule applies to it.
export const isPixelReferrerClean = (referrer: string, ownOrigin: string): boolean => {
   if (!referrer) return true;
   try {
      const url = new URL(referrer);
      if (url.origin !== ownOrigin) return true;
      return isPixelAddressClean({ pathname: url.pathname, search: url.search, hash: url.hash });
   } catch {
      return false;
   }
};

// Normalised exactly as the backend does it (models/ad_conversion_meta.go): the
// email is trimmed and lower-cased, an empty one is not sent, and the account id is
// hashed as `talktime:client:<id>`. Both are SHA-256 as lower-case hex.
export const normalisePixelEmail = (email: unknown): string =>
   typeof email === 'string' ? email.trim().toLowerCase() : '';

export const pixelExternalIdInput = (accountId: unknown): string => {
   const text = typeof accountId === 'number' ? String(accountId) : typeof accountId === 'string' ? accountId.trim() : '';
   return /^\d+$/.test(text) ? `talktime:client:${BigInt(text).toString()}` : '';
};

export const sha256Hex = async (value: string): Promise<string> => {
   const bytes = new TextEncoder().encode(value);
   const digest = await crypto.subtle.digest('SHA-256', bytes);
   return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const pixelEmailHash = async (email: unknown): Promise<string> => {
   const normalised = normalisePixelEmail(email);
   return normalised ? sha256Hex(normalised) : '';
};

export const pixelExternalIdHash = async (accountId: unknown): Promise<string> => {
   const input = pixelExternalIdInput(accountId);
   return input ? sha256Hex(input) : '';
};

export type PixelUser = { em?: string; external_id: string };

// The browser cookies are set for the widest domain the site can use, and a host
// cookie may sit beside them, so every candidate is cleared.
export const pixelCookieDomains = (hostname: string): (string | null)[] => {
   const domains = new Set<string | null>([null, PIXEL_COOKIE_DOMAIN]);
   const labels = hostname.split('.').filter(Boolean);
   if (labels.length) domains.add(hostname);
   for (let i = 0; i < labels.length - 1; i++) domains.add(`.${labels.slice(i).join('.')}`);
   return [...domains];
};

export const deletePixelCookies = (doc: { cookie: string }, hostname: string) => {
   for (const name of ['_fbp', '_fbc']) {
      for (const domain of pixelCookieDomains(hostname)) {
         doc.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/${domain ? `; Domain=${domain}` : ''}`;
      }
   }
};

const FBP_PATTERN = /^fb\.[0-2]\.\d{10,16}\.\d{1,24}$/;

// The browser id, only when it is well formed.
export const readPixelFbp = (cookie: string): string => {
   for (const part of cookie.split(';')) {
      const [key, ...rest] = part.trim().split('=');
      if (key === '_fbp') {
         const value = rest.join('=');
         return FBP_PATTERN.test(value) ? value : '';
      }
   }
   return '';
};
